import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { IFileSettings } from 'core-components/library/dragNDropFileUploader'
import { MeasureController } from 'core/analytics'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import {
  createContact,
  IContactSearchItem,
  IPartnerContact,
  selectContactsLoading,
  updateContact,
  uploadContactImage,
} from 'partner360/features/contacts'
import { useRefetchPlanningCockpitData } from 'partner360/features/financialCockpit/model/hooks/useRefetchPlanningCockpitData'
import { useHoldPlanSectionOpen } from 'partner360/lib/planSectionBusy'
import { useRecalculateCompleteness } from 'src/app/areas/partner360/hooks/useRecalculateCompleteness'

import { usePartnerContacts } from '../../hooks/usePartnerContacts'
import { TRANSLATION_KEYS } from '../../model/constants'
import { ContactsDeleteConfirm } from '../contactsDeleteConfirm/ContactsDeleteConfirm'
import { ContactSearch } from '../contactSearch/ContactSearch'
import { PartnerContactRow } from '../partnerContactRow/PartnerContactRow'

import styles from './ExtendedPartnerContacts.module.scss'

interface IExtendedPartnerContactsContentProps {
  partnerId: string
  programType: ProgramTypes
  metrics: MeasureController
}

export const ExtendedPartnerContacts = ({
  partnerId,
  programType,
  metrics,
}: IExtendedPartnerContactsContentProps) => {
  const [searchString, setSearchString] = useState('')
  const [editContact, setEditContact] = useState<IPartnerContact>()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const dispatch = useAppDispatch()
  const isEditMode = useSelector(selectPublishingMode)
  const { data } = usePartnerContacts({ programType, partnerId })
  const isLoading = useSelector(selectContactsLoading)

  useHoldPlanSectionOpen(isLoading)

  const filteredData = useMemo(() => {
    return data.filter((contact: IPartnerContact) => {
      if (!contact.FullName) {
        return true
      }

      return contact.FullName.toLowerCase().includes(searchString.toLowerCase())
    })
  }, [data, searchString])

  const handleSaveContact = useCallback(
    async (updatedContact: IPartnerContact, uploadedImage?: IFileSettings) => {
      let contactToSave = updatedContact

      // Only upload image if a new one was provided
      if (uploadedImage) {
        contactToSave = await dispatch(
          uploadContactImage({ fileSettings: uploadedImage, contact: updatedContact })
        ).unwrap()
      }

      // Save the contact (with updated image reference if image was uploaded)
      await dispatch(updateContact(contactToSave))
      setEditContact(undefined)
    },
    [dispatch]
  )

  const handleCancelEditing = useCallback(() => {
    setEditContact(undefined)
  }, [])

  const showDeleteContactDialog = useCallback((contact: IPartnerContact) => {
    setEditContact(contact)
    setShowDeleteDialog(true)
  }, [])

  const recalculatePartnerCompleteness = useRecalculateCompleteness()
  const refetchPlanningCockpitData = useRefetchPlanningCockpitData()

  /* The first contact added or the last one removed changes the plan's completeness, which the
     cockpit shows. */
  const recalculateCompleteness = useCallback(async () => {
    await recalculatePartnerCompleteness()
    refetchPlanningCockpitData()
  }, [recalculatePartnerCompleteness, refetchPlanningCockpitData])

  const handleSearch = useCallback((newSearchString: string) => {
    setSearchString(newSearchString)
  }, [])

  const deleteImage = useCallback(() => {
    setEditContact((prevState) => {
      if (prevState) {
        return {
          ...prevState,
          Image: '',
        }
      }

      return prevState
    })
  }, [])

  const handleAddToContacts = useCallback(
    async (contact: IContactSearchItem) => {
      await dispatch(
        createContact({
          roles: contact.roles,
          contactId: contact.contactId,
          email: contact.email,
          firstName: contact.firstName,
          lastName: contact.lastName,
          fullName: contact.fullName,
        })
      )

      if (!filteredData.length) {
        await recalculateCompleteness()
      }
    },
    [dispatch, filteredData, recalculateCompleteness]
  )

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading} />
      <ContactSearch
        partnerId={partnerId}
        partnerContacts={data}
        onSearch={handleSearch}
        onAddToContacts={handleAddToContacts}
      />

      <div className={styles.partnersCount}>
        <Translation
          text="jbp-partnercontactinfo--contacts-count-label"
          replaceParams={[
            {
              key: '{count}',
              value: filteredData.length.toString(),
            },
          ]}
        />
      </div>

      {filteredData.length > 0 && (
        <div className={styles.contactList}>
          {filteredData.map((contact: IPartnerContact) => {
            const isEditingContact = editContact !== undefined
            const isEditingThisContact =
              !showDeleteDialog && isEditingContact && editContact.EntryId === contact.EntryId
            const hideControls = !isEditMode || (isEditingContact && !isEditingThisContact)

            return (
              <PartnerContactRow
                key={contact.UID}
                isContactEditing={isEditingThisContact}
                hideControls={hideControls}
                contact={isEditingThisContact ? editContact : contact}
                programType={programType}
                metrics={metrics}
                onContactChange={setEditContact}
                onEditContact={setEditContact}
                onCancelEditing={handleCancelEditing}
                onDeleteContact={showDeleteContactDialog}
                onDeleteImage={deleteImage}
                onSaveContact={handleSaveContact}
              />
            )
          })}
        </div>
      )}

      {filteredData.length === 0 && (
        <div className={styles.noContacts}>
          <RenderWhen condition={searchString.length === 0}>
            <Translation text={TRANSLATION_KEYS.emptyList} />
          </RenderWhen>
          <RenderWhen condition={searchString.length > 0}>
            <Translation text={TRANSLATION_KEYS.emptySearchResult} />
          </RenderWhen>
        </div>
      )}

      <ContactsDeleteConfirm
        showDialog={showDeleteDialog}
        setShowDialog={setShowDeleteDialog}
        item={editContact || null}
        setItem={() => setEditContact(undefined)}
        triggerCompleteness={recalculateCompleteness}
      />
    </div>
  )
}
